import { useAcceptReservationChange } from "@/api/reservation/useAcceptReservationChange";
import { useDeclineReservationChange } from "@/api/reservation/useDeclineReservationChange";
import { useGetMyUpcomingReservations } from "@/api/user/useGetMyUpcomingReservations";
import { StyledActionButton } from "@/components/shared/StyledActionButton";
import { Box, Center, Group, Stack, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconArrowNarrowDown, IconCalendar, IconMapPin } from "@tabler/icons-react";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

export function ReservationChangeRequestPage() {
  const { reservationId } = useParams<{ reservationId: string }>();
  const navigate = useNavigate();

  const { data: reservations, isLoading } = useGetMyUpcomingReservations();
  const acceptMutation = useAcceptReservationChange();
  const declineMutation = useDeclineReservationChange();

  const reservation = useMemo(() => {
    if (!reservations || !reservationId) return null;
    return reservations.find((r) => r.id === parseInt(reservationId));
  }, [reservations, reservationId]);

  if (isLoading) {
    return (
      <Center h="100vh">
        <Text>로딩 중...</Text>
      </Center>
    );
  }

  if (
    !reservation ||
    (reservation.status !== "CHANGE_REQUESTED_BY_ADMIN" &&
      reservation.status !== "PREFERENCE_OVERRIDE_REQUESTED")
  ) {
    return (
      <Center h="100vh">
        <Text>요청을 찾을 수 없습니다.</Text>
      </Center>
    );
  }

  const handleAccept = async () => {
    try {
      await acceptMutation.mutateAsync(reservation.id);
      navigate("/home/reservation/upcoming");
    } catch (error) {
      notifications.show({
        title: "일정 변경을 수락하지 못했습니다",
        message:
          error instanceof Error ? error.message : "오류가 발생했습니다. 잠시 후 다시 시도해주세요.",
        color: "red",
      });
      console.error("Failed to accept change:", error);
    }
  };

  const handleDecline = async () => {
    try {
      await declineMutation.mutateAsync(reservation.id);
      navigate("/reservation-change-declined");
    } catch (error) {
      notifications.show({
        title: "일정 변경을 거절하지 못했습니다",
        message:
          error instanceof Error ? error.message : "오류가 발생했습니다. 잠시 후 다시 시도해주세요.",
        color: "red",
      });
    }
  };

  const originalEvent = reservation.event;
  const newEventRequested = reservation.newEventRequested;
  const isPreferenceOverride = reservation.status === "PREFERENCE_OVERRIDE_REQUESTED";

  const messageForAfterEventConfirmed = `
아쉽게도, 함께 하기로 했던 분들이 부득이하게 참석이 어렵다고 전해주셨어요. 🥹

더 좋은 매칭을 위해 일정 변경을 제안드려요. 💌

새 일정이 어려우시면, 자동으로 전액 환불 처리되니
걱정하지 마세요 ☺️
  `;
  const messageForBeforeEventConfirmed = `
아쉽게도, 이번에 신청하신 곳의 모든 자리가 마감 되어버렸어요. 🥹

더 좋은 매칭을 위해 일정 변경을 제안드려요. 💌

새 일정이 어려우시면, 자동으로 전액 환불 처리되니
걱정하지 마세요 ☺️
  `;
  return (
    <Box p="lg" h="100vh">
      <Stack gap="xl" justify="space-between" h="100%">
        <Stack gap="xl">
          <Title order={2} ta="center" pt="md">
            예약 변경 요청
          </Title>

          <Box mt="25%">
            <Text size="lg" style={{ whiteSpace: "pre-wrap" }} mb="xl">
              {isPreferenceOverride
                ? messageForBeforeEventConfirmed
                : messageForAfterEventConfirmed}
            </Text>
            {/* Original Event or Preferences */}
            <Box
              p="lg"
              style={(theme) => ({
                flexGrow: 1,
                backgroundColor: "#202f37",
                borderRadius: theme.radius.lg,
                border: "1px solid #555555",
                padding: "0.5rem",
              })}
            >
              <Text fw={600} size="lg" mb="md">
                {isPreferenceOverride ? "신청하신 선호 일정" : "기존 예약"}
              </Text>
              <Stack gap="sm">
                {isPreferenceOverride ? (
                  <>
                    {/* Show all preferred times */}
                    <Group gap="sm" align="flex-start">
                      <IconCalendar size={20} style={{ marginTop: "2px" }} />
                      <Stack gap="xs">
                        <Text fw={500}>선호 일정:</Text>
                        {reservation.preferredTimes.map((time, index) => (
                          <Text key={index} size="sm" pl="md">
                            •{" "}
                            {new Date(time).toLocaleString("ko-KR", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                              weekday: "long",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </Text>
                        ))}
                      </Stack>
                    </Group>
                    {/* Show all preferred locations */}
                    <Group gap="sm" align="flex-start">
                      <IconMapPin size={20} style={{ marginTop: "2px" }} />
                      <Stack gap="xs">
                        <Text fw={500}>선호 장소:</Text>
                        {reservation.preferredLocationNames.map((location, index) => (
                          <Text key={index} size="sm" pl="md">
                            • {location}
                          </Text>
                        ))}
                      </Stack>
                    </Group>
                  </>
                ) : (
                  <>
                    <Group gap="sm">
                      <IconCalendar size={20} />
                      <Text>
                        {originalEvent
                          ? new Date(originalEvent.startTime).toLocaleString("ko-KR", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                              weekday: "long",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "날짜 정보 없음"}
                      </Text>
                    </Group>
                    <Group gap="sm">
                      <IconMapPin size={20} />
                      <Text>{originalEvent?.location?.name || "위치 정보 없음"}</Text>
                    </Group>
                  </>
                )}
              </Stack>
            </Box>

            <Center>
              <IconArrowNarrowDown size={30} />
            </Center>

            {/* New Event */}
            <Box
              p="lg"
              style={(theme) => ({
                flexGrow: 1,
                backgroundColor: "#202f37",
                borderRadius: theme.radius.lg,
                border: "1px solid #555555",
                padding: "0.5rem",
              })}
            >
              <Text fw={600} size="lg" mb="md">
                {isPreferenceOverride ? "배정된 일정" : "새로운 예약"}
              </Text>
              <Stack gap="sm">
                <Group gap="sm">
                  <IconCalendar size={20} />
                  <Text>
                    {newEventRequested
                      ? new Date(newEventRequested.startTime).toLocaleString("ko-KR", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          weekday: "long",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "날짜 정보 없음"}
                  </Text>
                </Group>
                <Group gap="sm">
                  <IconMapPin size={20} />
                  <Text>{newEventRequested?.location?.name || "위치 정보 없음"}</Text>
                </Group>
              </Stack>
            </Box>
          </Box>
        </Stack>

        {/* Action Buttons - Aligned to Bottom */}
        <Stack gap="md" pb="md">
          <StyledActionButton
            size="xl"
            onClick={handleAccept}
            loading={acceptMutation.isPending}
            disabled={declineMutation.isPending}
            width="100%"
          >
            수락
          </StyledActionButton>
          <StyledActionButton
            size="xl"
            buttonColor="red"
            textColor="white"
            onClick={handleDecline}
            loading={declineMutation.isPending}
            disabled={acceptMutation.isPending}
            width="100%"
          >
            거절
          </StyledActionButton>
        </Stack>
      </Stack>
    </Box>
  );
}
